import axios from 'axios';
// config
import { HOST_API_KEY } from '../config-global';
import { getAuthNamespace, getImpersonationScopeFromToken } from '../auth/impersonation';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API_KEY });
let refreshRequest = null;

const clearAuthentication = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  delete axiosInstance.defaults.headers.common.Authorization;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isPublicRequest = originalRequest?.publicRequest;
    const refreshToken = localStorage.getItem('refreshToken');
    const sessionScope = getImpersonationScopeFromToken(refreshToken) || 'franchise';
    const authNamespace = getAuthNamespace(sessionScope);
    const isRefreshRequest = originalRequest?.url?.includes(`/${authNamespace}/refresh-tokens`);

    if (
      error.response?.status === 401 &&
      !isPublicRequest &&
      refreshToken &&
      !originalRequest?._retry &&
      !isRefreshRequest
    ) {
      originalRequest._retry = true;
      try {
        if (!refreshRequest) {
          refreshRequest = axios.post(`${process.env.REACT_APP_BACKEND_URL}/${authNamespace}/refresh-tokens`, {
            refreshToken,
            browserInfo: { userAgent: window.navigator.userAgent },
          }).finally(() => {
            refreshRequest = null;
          });
        }
        const response = await refreshRequest;
        const tokens = response.data?.tokens || response.data;
        const accessToken = tokens?.access?.token;
        const nextRefreshToken = tokens?.refresh?.token;
        if (!accessToken || !nextRefreshToken) throw new Error('Invalid refresh response');

        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', nextRefreshToken);
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        clearAuthentication();
        window.location.assign('/auth/login');
        return Promise.reject(refreshError?.response?.data || refreshError);
      }
    }

    if (error.response?.status === 401 && !isPublicRequest) {
      clearAuthentication();
      window.location.assign('/auth/login');
    }

    return Promise.reject(
      (error.response && error.response.data) || error || 'Something went wrong'
    );
  }
);

export default axiosInstance;
